import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { of, tap } from 'rxjs';
import { AdminNonConformanceRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import {
  ICustomPropertyDefinition,
  ICustomPropertyResponse,
} from '../../models/custom-property/custom-property.model';
import { CustomPropertyApiService } from '../../services/custom-property/custom-property-api.service';
import {
  ClearCurrentCustomProperty,
  CreateCustomProperty,
  DeleteCustomProperty,
  LoadCustomProperties,
  LoadCustomPropertyById,
  LoadCustomPropertyByVersion,
  RestoreCustomProperty,
  UpdateCustomProperty,
} from './custom-property.actions';

export const NC_CUSTOM_PROPERTY_STATE_TOKEN = new StateToken<CustomPropertyStateModel>(
  'CustomPropertyState'
);

export interface CustomPropertyStateModel {
  customProperties: ICustomPropertyDefinition[];
  customPropertyTotalPages: number;
  currentCustomProperty: ICustomPropertyDefinition | undefined;
  customPropertyVersions: Record<string, ICustomPropertyResponse>;
}

@State<CustomPropertyStateModel>({
  name: NC_CUSTOM_PROPERTY_STATE_TOKEN,
  defaults: {
    customProperties: [],
    customPropertyTotalPages: 0,
    currentCustomProperty: undefined,
    customPropertyVersions: {},
  },
})
@Injectable()
export class CustomPropertyState {
  constructor(private service: CustomPropertyApiService) {}

  @Action(LoadCustomProperties)
  loadCustomProperties(
    { getState, setState }: StateContext<CustomPropertyStateModel>,
    { filter }: LoadCustomProperties
  ) {
    return this.service.loadCustomProperties(filter).pipe(
      tap((response: ApiResponse<ICustomPropertyDefinition[]>) => {
        const state = getState();
        setState({
          ...state,
          customProperties: response.data,
          customPropertyTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadCustomPropertyById, { cancelUncompleted: true })
  loadCustomPropertyById(
    { getState, setState }: StateContext<CustomPropertyStateModel>,
    { id }: LoadCustomPropertyById
  ) {
    return this.service.loadCustomPropertyById(id).pipe(
      tap((customPropertyResponse: ICustomPropertyResponse) => {
        // Convert ICustomPropertyResponse to ICustomPropertyDefinition
        const customProperty: ICustomPropertyDefinition = {
          id: customPropertyResponse.id,
          name: customPropertyResponse.name,
          deleted: customPropertyResponse.deleted,
          possibleAnswers: customPropertyResponse.possibleAnswers.map((answer) => answer.text),
        };
        setState(
          patch({
            currentCustomProperty: customProperty,
          })
        );
      })
    );
  }

  @Action(LoadCustomPropertyByVersion)
  loadCustomPropertyByVersion(
    { getState, setState }: StateContext<CustomPropertyStateModel>,
    { id, versionId }: LoadCustomPropertyByVersion
  ) {
    const state = getState();

    // Check if version is already cached
    if (state.customPropertyVersions[versionId]) {
      return of(null);
    }

    return this.service.loadCustomPropertyByVersion(id, versionId).pipe(
      tap((customPropertyResponse: ICustomPropertyResponse) => {
        setState(
          patch({
            customPropertyVersions: patch({
              [versionId]: customPropertyResponse,
            }),
          })
        );
      })
    );
  }

  @Action(CreateCustomProperty)
  @OnSuccess({ url: AdminNonConformanceRoutes.CustomProperties, message: 'customPropertyAdded' })
  createCustomProperty(
    { setState }: StateContext<CustomPropertyStateModel>,
    { customProperty }: CreateCustomProperty
  ) {
    return this.service.createCustomProperty(customProperty).pipe(
      tap((response: ICustomPropertyDefinition) => {
        setState(
          patch({
            customProperties: insertItem<ICustomPropertyDefinition>(response),
          })
        );
      })
    );
  }

  @Action(UpdateCustomProperty)
  @OnSuccess({ url: AdminNonConformanceRoutes.CustomProperties, message: 'customPropertyUpdated' })
  updateCustomProperty(
    { setState }: StateContext<CustomPropertyStateModel>,
    { customProperty }: UpdateCustomProperty
  ) {
    return this.service.updateCustomProperty(customProperty).pipe(
      tap(() => {
        setState(
          patch({
            customProperties: updateItem<ICustomPropertyDefinition>(
              (toUpdate) => toUpdate?.id === customProperty.id,
              customProperty
            ),
          })
        );
      })
    );
  }

  @Action(DeleteCustomProperty, { cancelUncompleted: true })
  @OnSuccess({ message: 'customPropertyDeleted', url: AdminNonConformanceRoutes.CustomProperties })
  deleteCustomProperty(
    { setState }: StateContext<CustomPropertyStateModel>,
    { id }: DeleteCustomProperty
  ) {
    return this.service.deleteCustomProperty(id).pipe(
      tap(() => {
        setState(
          patch({
            customProperties: removeItem<ICustomPropertyDefinition>((item) => item?.id === id),
          })
        );
      })
    );
  }

  @Action(RestoreCustomProperty, { cancelUncompleted: true })
  @OnSuccess({ message: 'customPropertyRestored', url: AdminNonConformanceRoutes.CustomProperties })
  restoreCustomProperty(
    { setState }: StateContext<CustomPropertyStateModel>,
    { id }: RestoreCustomProperty
  ) {
    return this.service.restoreCustomProperty(id).pipe(
      tap(() => {
        // Refresh the list to show the restored item
        setState(
          patch({
            customProperties: updateItem<ICustomPropertyDefinition>(
              (toUpdate) => toUpdate?.id === id,
              (item) => (item ? { ...item, deleted: false } : item)
            ),
          })
        );
      })
    );
  }

  @Action(ClearCurrentCustomProperty)
  clearCurrentCustomProperty({ setState }: StateContext<CustomPropertyStateModel>) {
    setState(
      patch({
        currentCustomProperty: undefined,
      })
    );
  }

  @Action(ResetCurrentFilter)
  resetCurrentFilter({ setState }: StateContext<CustomPropertyStateModel>) {
    setState(
      patch({
        customProperties: [],
        customPropertyTotalPages: 0,
      })
    );
  }

  @Selector()
  static customProperties(state: CustomPropertyStateModel): ICustomPropertyDefinition[] {
    return state.customProperties;
  }

  @Selector()
  static getCustomPropertiesTotalPages(state: CustomPropertyStateModel): number {
    return state.customPropertyTotalPages;
  }

  @Selector()
  static getSelectedCustomProperty(
    state: CustomPropertyStateModel
  ): ICustomPropertyDefinition | undefined {
    return state.currentCustomProperty;
  }

  @Selector()
  static getCustomPropertyVersions(
    state: CustomPropertyStateModel
  ): Record<string, ICustomPropertyResponse> {
    return state.customPropertyVersions;
  }

  static getCustomPropertyVersion(versionId: string) {
    return createSelector(
      [CustomPropertyState.getCustomPropertyVersions],
      (versions: Record<string, ICustomPropertyResponse>) => {
        return versions[versionId];
      }
    );
  }
}
